import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

interface SmartNavigationConfig {
  preloading?: {
    enabled: boolean;
    strategy: 'aggressive' | 'conservative' | 'smart';
    preloadDistance: number;
  };
  navigation?: {
    wheel: boolean;
    keyboard: boolean;
    clickZones: boolean;
  };
}

interface UseSmartNavigationProps<T> {
  currentItem: T | null;
  allItems: T[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  loadMore?: () => void;
  onNavigate?: (item: T) => void;
  getItemId: (item: T) => string;
  config?: SmartNavigationConfig;
}

const DEFAULT_CONFIG: SmartNavigationConfig = {
  preloading: {
    enabled: true,
    strategy: 'smart',
    preloadDistance: 2
  },
  navigation: {
    wheel: true,
    keyboard: true,
    clickZones: true
  }
};

export const useSmartNavigation = <T>({
  currentItem,
  allItems,
  hasNextPage = false,
  isFetchingNextPage = false,
  loadMore,
  onNavigate,
  getItemId,
  config = DEFAULT_CONFIG
}: UseSmartNavigationProps<T>) => {
  // Memoize validItems to prevent unnecessary recreations
  const validItems = useMemo(() => {
    return Array.isArray(allItems) && allItems.length > 0 ? allItems : [];
  }, [allItems]);
  
  // Memoize currentIndex calculation
  const currentIndex = useMemo(() => {
    return currentItem ? validItems.findIndex(item => item && getItemId(item) === getItemId(currentItem)) : -1;
  }, [currentItem, validItems, getItemId]);
  
  // Navigation state
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < validItems.length - 1 && currentIndex !== -1;
  
  // Ref-based index tracking for stability during data changes
  const currentIndexRef = useRef(currentIndex);
  
  useEffect(() => {
    if (currentIndex !== -1) {
      currentIndexRef.current = currentIndex;
    }
  }, [currentIndex]);

  // Sophisticated preloading logic - the crown jewel
  useEffect(() => {
    if (!config.preloading?.enabled || !loadMore) return;
    
    const { strategy, preloadDistance } = config.preloading;
    const shouldPreload = () => {
      switch (strategy) {
        case 'aggressive':
          // Load when approaching last quarter
          return currentIndex >= validItems.length - Math.ceil(validItems.length * 0.25);
        case 'conservative':
          // Load only when at last item
          return currentIndex === validItems.length - 1;
        case 'smart':
        default:
          // Load when within preloadDistance of end
          return currentIndex >= validItems.length - preloadDistance;
      }
    };

    if (shouldPreload() && hasNextPage && !isFetchingNextPage) {
      console.log(`[useSmartNavigation] ${strategy} preloading triggered at index ${currentIndex}`);
      loadMore();
    }
  }, [currentIndex, validItems.length, hasNextPage, isFetchingNextPage, loadMore, config.preloading]);

  // Navigation handlers with sophisticated logic
  const handlePrevious = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    }
    
    if (hasPrevious && onNavigate) {
      const prevIndex = Math.max(0, currentIndexRef.current - 1);
      const items = Array.isArray(allItems) && allItems.length > 0 ? allItems : [];
      if (items[prevIndex]) {
        onNavigate(items[prevIndex]);
      }
    }
  }, [hasPrevious, onNavigate, allItems]);

  const handleNext = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    }
    
    const items = Array.isArray(allItems) && allItems.length > 0 ? allItems : [];
    
    if (hasNext && onNavigate) {
      const nextIndex = Math.min(items.length - 1, currentIndexRef.current + 1);
      if (items[nextIndex]) {
        onNavigate(items[nextIndex]);
        
        // Smart preloading on navigation
        if (config.preloading?.enabled && loadMore) {
          const { strategy, preloadDistance } = config.preloading;
          if (strategy === 'smart' && nextIndex >= items.length - preloadDistance && hasNextPage && !isFetchingNextPage) {
            console.log('[useSmartNavigation] Smart preloading on navigation');
            loadMore();
          }
        }
      }
    } else if (!hasNext && hasNextPage && !isFetchingNextPage && loadMore) {
      // At end but more available - trigger load
      console.log('[useSmartNavigation] Loading more at end');
      loadMore();
    }
  }, [hasNext, onNavigate, allItems, config.preloading, loadMore, hasNextPage, isFetchingNextPage]);

  // Mouse wheel navigation with direction detection
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!config.navigation?.wheel) return;
    
    if (e.deltaY > 0) {
      // Scroll down/right - go to next
      if (hasNext) {
        handleNext();
      } else if (!hasNext && hasNextPage && !isFetchingNextPage && loadMore) {
        console.log('[useSmartNavigation] Wheel triggered pagination');
        loadMore();
      }
    } else if (e.deltaY < 0 && hasPrevious) {
      // Scroll up/left - go to previous
      handlePrevious();
    }
  }, [config.navigation?.wheel, hasNext, hasPrevious, handleNext, handlePrevious, hasNextPage, isFetchingNextPage, loadMore]);

  // Keyboard navigation
  useEffect(() => {
    if (!config.navigation?.keyboard) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept keyboard events if user is typing in an input field
      const target = e.target as HTMLElement;
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true' ||
        target.closest('[contenteditable="true"]') ||
        target.closest('input') ||
        target.closest('textarea')
      );
      
      if (isTyping) {
        return; // Don't handle keyboard navigation when user is typing
      }
      
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          e.preventDefault();
          handleNext();
          break;
        case 'Escape':
          // Allow parent to handle escape
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [config.navigation?.keyboard, handlePrevious, handleNext]);

  return {
    // Navigation state
    currentIndex,
    hasPrevious,
    hasNext,
    canNavigateNext: hasNext || (hasNextPage && !isFetchingNextPage),
    
    // Navigation handlers
    handlePrevious,
    handleNext,
    handleWheel,
    
    // Items info
    totalItems: validItems.length,
    isAtEnd: currentIndex === validItems.length - 1,
    isAtStart: currentIndex === 0,
    
    // Loading state
    isLoadingMore: isFetchingNextPage,
    hasMoreToLoad: hasNextPage
  };
}; 